import { useEffect, useRef, useState } from "react";
import { Bell, Trash2, X, Share2, Check } from "lucide-react";
import { useStore } from "../context/StoreContext";
import BragCard from "./BragCard";
import sfx from "../utils/sound";
import AdBanner from "./AdBanner";

const timeAgo = (ts) => {
  const s = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

const NotificationCenter = () => {
  const {
    notifications,
    markAllNotificationsRead,
    clearNotifications,
    removeNotification,
    name,
  } = useStore();
  const [open, setOpen] = useState(false);
  const [bragTarget, setBragTarget] = useState(null);
  const panelRef = useRef(null);

  const list = notifications || [];
  const unread = list.filter((n) => !n.read).length;

  // Close on outside click / Esc
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const toggle = () => {
    sfx.click();
    if (!open && unread > 0) markAllNotificationsRead();
    setOpen((o) => !o);
  };

  return (
    <>
      <div ref={panelRef} style={{ position: "relative" }}>
        <button
          type="button"
          onClick={toggle}
          aria-label={`Notifications${unread ? ` (${unread} unread)` : ""}`}
          data-testid="notif-bell"
          className="rounded-full flex items-center justify-center relative shrink-0"
          style={{
            width: 36,
            height: 36,
            background: unread > 0 ? "#FFB400" : "#fff",
            color: "#2b2b2b",
            border: "3px solid #2b2b2b",
            boxShadow: "0 3px 0 #2b2b2b",
          }}
        >
          <Bell size={18} strokeWidth={3} />
          {unread > 0 && (
            <span
              className="absolute -top-1 -right-1 rounded-full bg-white text-black font-bold flex items-center justify-center"
              style={{
                minWidth: 22,
                height: 22,
                padding: "0 6px",
                border: "2px solid #2b2b2b",
                fontSize: 12,
              }}
              data-testid="notif-unread-count"
            >
              {unread}
            </span>
          )}
        </button>

        {open && (
          <div
            className="chunky-card"
            data-testid="notif-panel"
            style={{
              position: "absolute",
              top: 54,
              right: 0,
              width: "min(90vw, 340px)",
              zIndex: 70,
              background: "#FFF9E6",
              padding: 0,
              overflow: "hidden",
            }}
          >
            <div
              className="flex items-center justify-between px-3 py-2"
              style={{ borderBottom: "3px solid #2b2b2b", background: "#FFB400" }}
            >
              <div className="font-display text-base flex items-center gap-2">
                <Bell size={16} strokeWidth={3} />
                Notifications
              </div>
              <div className="flex items-center gap-2">
                {list.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      sfx.click();
                      clearNotifications();
                    }}
                    aria-label="Clear all"
                    data-testid="notif-clear-all"
                    className="rounded-full flex items-center justify-center"
                    style={{
                      width: 30,
                      height: 30,
                      background: "#fff",
                      border: "2px solid #2b2b2b",
                    }}
                  >
                    <Trash2 size={14} strokeWidth={3} />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Close"
                  data-testid="notif-close"
                  className="rounded-full flex items-center justify-center"
                  style={{
                    width: 30,
                    height: 30,
                    background: "#fff",
                    border: "2px solid #2b2b2b",
                  }}
                >
                  <X size={14} strokeWidth={3} />
                </button>
              </div>
            </div>

            <div style={{ maxHeight: 360, overflowY: "auto" }}>
              {/* Sponsored — pinned above the list so kids see it once per open. */}
              <div className="p-2" data-testid="notif-ad-slot">
                <AdBanner slot="notifications" />
              </div>
              {list.length === 0 ? (
                <div
                  className="p-5 text-center"
                  data-testid="notif-empty"
                  style={{ color: "#484848" }}
                >
                  <div className="text-4xl mb-2" aria-hidden="true">
                    🌟
                  </div>
                  <div className="font-display">You&apos;re all caught up!</div>
                  <p className="text-xs mt-1">
                    Rewards, sticker unlocks and big wins will show up here.
                  </p>
                </div>
              ) : (
                <ul className="flex flex-col gap-2 p-2">
                  {list.map((n) => (
                    <li
                      key={n.id}
                      data-testid={`notif-item-${n.id}`}
                      data-kind={n.kind}
                      className="chunky-card flex items-start gap-2 p-2"
                      style={{
                        background: n.read ? "#fff" : "#FFF3B0",
                        boxShadow: "0 3px 0 #2b2b2b",
                      }}
                    >
                      <div
                        aria-hidden="true"
                        className="rounded-full flex items-center justify-center shrink-0"
                        style={{
                          width: 34,
                          height: 34,
                          background: "#F368E0",
                          color: "#fff",
                          border: "2px solid #2b2b2b",
                          fontSize: 18,
                        }}
                      >
                        {n.emoji || "🔔"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-display text-sm leading-tight">
                          {n.title}
                        </div>
                        {n.body && (
                          <div
                            className="text-xs mt-0.5"
                            style={{ color: "#484848" }}
                          >
                            {n.body}
                          </div>
                        )}
                        <div
                          className="text-[10px] mt-1"
                          style={{ color: "#8a8a8a" }}
                        >
                          {timeAgo(n.at)}
                        </div>
                        {n.kind === "brag" && (
                          <button
                            type="button"
                            onClick={() => setBragTarget(n)}
                            className="btn-3d btn-sm btn-pink mt-2"
                            data-testid={`notif-brag-${n.id}`}
                          >
                            <Share2 size={12} strokeWidth={3} />
                            Make card
                          </button>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeNotification(n.id)}
                        aria-label="Dismiss"
                        data-testid={`notif-dismiss-${n.id}`}
                        className="rounded-full flex items-center justify-center shrink-0"
                        style={{
                          width: 26,
                          height: 26,
                          background: "#fff",
                          border: "2px solid #2b2b2b",
                        }}
                      >
                        <Check size={12} strokeWidth={3} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>

      <BragCard
        open={!!bragTarget}
        onClose={() => setBragTarget(null)}
        title={bragTarget?.title || "Big Win!"}
        subtitle={bragTarget?.body || "Playing on 67Cafe"}
        emoji={bragTarget?.emoji || "🏆"}
        coins={bragTarget?.coins || 0}
        name={name || "67Cafe Kid"}
      />
    </>
  );
};

export default NotificationCenter;
